import React, { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../context/I18nContext';
import './InstallPwaGate.css';

const DISMISS_KEY = 'poupix_install_prompt_dismissed';

const isStandaloneMode = () => {
  const inStandaloneDisplay = window.matchMedia?.('(display-mode: standalone)')?.matches;
  const inIosStandalone = window.navigator.standalone === true;
  return Boolean(inStandaloneDisplay || inIosStandalone);
};

const isMobileDevice = () => {
  const ua = navigator.userAgent || '';
  const uaMatch = /Android|iPhone|iPad|iPod/i.test(ua);
  const coarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches;
  return Boolean(uaMatch || (coarsePointer && window.innerWidth <= 1024));
};

const ShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ display: 'inline', verticalAlign: 'middle', margin: '0 2px' }}>
    <path d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InstallPwaGate = () => {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [mounted, setMounted] = useState(false);

  const isIos = useMemo(() => /iPhone|iPad|iPod/i.test(navigator.userAgent || ''), []);

  useEffect(() => {
    setMounted(true);

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      const dismissed = localStorage.getItem(DISMISS_KEY) === '1';
      if (!dismissed && !isStandaloneMode()) {
        setVisible(true);
      }
    };

    const onAppInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      localStorage.removeItem(DISMISS_KEY);
    };

    if (isIos && isMobileDevice() && !isStandaloneMode()) {
      const dismissed = localStorage.getItem(DISMISS_KEY) === '1';
      if (!dismissed) {
        setVisible(true);
      }
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, [isIos]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (_) {
      // no-op
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  if (window.Capacitor?.isNativePlatform()) return null;
  if (!mounted || !visible || isStandaloneMode()) return null;

  const showIosSteps = isIos && !deferredPrompt;

  return (
    <div className="ig-overlay" role="dialog" aria-modal="true" aria-label={t('installGate.aria')}>
      <div className="ig-card">

        {/* Header */}
        <div className="ig-header">
          <div className="ig-app-info">
            <img src="/poupix-logo-small.png" alt="Poupix" className="ig-app-icon" />
            <div>
              <p className="ig-app-name">Poupix</p>
              <p className="ig-app-sub">{t('installGate.headerSub')}</p>
            </div>
          </div>
          <button className="ig-close" onClick={handleDismiss} aria-label={t('installGate.close')}>✕</button>
        </div>

        {/* Steps (iOS) or install button (Android) */}
        {showIosSteps ? (
          <>
            <ol className="ig-steps">
              <li className="ig-step">
                <span className="ig-step-num">1</span>
                <span className="ig-step-text">
                  {t('installGate.step1pre')}<ShareIcon /><strong className="ig-share-word">{t('installGate.step1share')}</strong>{t('installGate.step1post')}
                </span>
              </li>
              <li className="ig-step">
                <span className="ig-step-num">2</span>
                <span className="ig-step-text">{t('installGate.step2pre')}<strong>"{t('installGate.step2action')}"</strong></span>
              </li>
              <li className="ig-step">
                <span className="ig-step-num">3</span>
                <span className="ig-step-text">{t('installGate.step3pre')}<strong>"{t('installGate.step3action')}"</strong>{t('installGate.step3post')}</span>
              </li>
            </ol>

            <div className="ig-arrow-hint">
              <p className="ig-arrow-text">{t('installGate.shareBelow')}</p>
              <span className="ig-arrow-icon" aria-hidden="true">↓</span>
            </div>

            <button className="ig-already-btn" onClick={handleDismiss}>
              {t('installGate.alreadyInstalled')}
            </button>
          </>
        ) : (
          <>
            <p className="ig-android-text">{t('installGate.androidText')}</p>
            <button className="ig-install-btn" onClick={handleInstall}>
              {t('installGate.install')}
            </button>
            <button className="ig-skip-btn" onClick={handleDismiss}>
              {t('installGate.continueBrowser')}
            </button>
          </>
        )}

      </div>
    </div>
  );
};

export default InstallPwaGate;
